import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  addSubSection,
  updateSubSection,
  getSubSectionById,
} from "../../../services/subSectionsService";
import { getSectionsList } from "../../../services/sectionsService";
import { validateSubSection } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { toast } from "react-toastify";
import { handleErrors } from "../../../utils/errorHandler";
import AiAssistantOffcanvas from "../../AI/AiAssistantOffcanvas";
import { SUB_SECTION_AI_RESPONSE_FIELD_MAP } from "../../AI/AIResponseFieldMap";
import FormSideDrawer from "../../Common/FormSideDrawer/FormSideDrawer";

const INITIAL_FORM_STATE = {
  sectionId: "",
  subSectionName: "",
  subSectionSlug: "",
  subSectionDescription: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  displayOrder: "",
  isFeatured: false,
  isFeaturedHome: false,
  isPremium: false,
};

const parseBoolFromApi = (data, ...keys) => {
  for (const key of keys) {
    const value = data?.[key];
    if (value !== undefined && value !== null) {
      return value === true || value === "true" || value === 1;
    }
  }
  return false;
};

const INITIAL_ERRORS_STATE = {
  sectionId: "",
  subSectionName: "",
  subSectionSlug: "",
  displayOrder: "",
};

const mapSubSectionResponse = (data) => ({
  sectionId: String(data.sectionId ?? data.SectionId ?? ""),
  subSectionName: data.subSectionName || data.SubSectionName || "",
  subSectionSlug: data.subSectionSlug || data.SubSectionSlug || "",
  subSectionDescription:
    data.subSectionDescription || data.SubSectionDescription || "",
  metaTitle: data.metaTitle || data.MetaTitle || "",
  metaDescription: data.metaDescription || data.MetaDescription || "",
  metaKeywords: data.metaKeywords || data.MetaKeywords || "",
  isFeatured: parseBoolFromApi(
    data,
    "isFeatured",
    "IsFeatured",
    "featured",
    "Featured"
  ),
  isFeaturedHome: parseBoolFromApi(
    data,
    "isFeaturedHome",
    "IsFeaturedHome",
    "featuredHome",
    "FeaturedHome"
  ),
  isPremium: parseBoolFromApi(data, "isPremium", "IsPremium"),
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
        ? String(data.DisplayOrder)
        : "",
});

export const AddSubSection = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedSubSection,
  setEditMode,
  isOpen = false,
  onClose,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [sections, setSections] = useState([]);
  const slugManuallyEdited = useRef(false);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    slugManuallyEdited.current = false;
  }, []);

  const closeDrawer = useCallback(() => {
    resetForm();
    setSelectedSubSection?.(null);
    setEditMode?.(false);
    onClose?.();
  }, [resetForm, setSelectedSubSection, setEditMode, onClose]);

  useEffect(() => {
    const loadSections = async () => {
      try {
        const response = await getSectionsList();
        const list = response.data?.result || response.data || [];
        setSections(Array.isArray(list) ? list : []);
      } catch (error) {
        handleErrors(error);
      }
    };
    loadSections();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      const subSectionId = initialData?.id ?? initialData?.Id;
      if (editMode && subSectionId) {
        try {
          const data = await getSubSectionById(subSectionId);
          setFormData(mapSubSectionResponse(data));
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
      setErrors(INITIAL_ERRORS_STATE);
    };
    fetchData();
  }, [editMode, initialData, isOpen]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "subSectionName") {
      setFormData((prev) => ({
        ...prev,
        subSectionName: value,
        ...(slugManuallyEdited.current
          ? {}
          : { subSectionSlug: generateSlug(value) }),
      }));
    } else if (name === "subSectionSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, subSectionSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: Boolean(checked) }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } =
        validateSubSection(formData);
      setErrors(validationErrors);
      if (!valid) return;

      const subSectionId = initialData?.id ?? initialData?.Id;
      const payload = {
        sectionId: Number(formData.sectionId),
        subSectionName: formData.subSectionName.trim(),
        subSectionSlug: formData.subSectionSlug.trim(),
        subSectionDescription: formData.subSectionDescription?.trim() || "",
        metaTitle: formData.metaTitle?.trim() || "",
        metaDescription: formData.metaDescription?.trim() || "",
        metaKeywords: formData.metaKeywords?.trim() || "",
        featured: Boolean(formData.isFeatured),
        featuredHome: Boolean(formData.isFeaturedHome),
        isPremium: Boolean(formData.isPremium),
        displayOrder: Number(formData.displayOrder),
      };

      if (editMode) {
        payload.id = subSectionId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateSubSection : addSubSection;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Sub section ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        onSuccess?.();
        setEditMode?.(false);
        setSelectedSubSection?.(null);
        onClose?.();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      setSelectedSubSection,
      onClose,
      resetForm,
    ]
  );

  const buildSubSectionDynamicFields = useCallback(
    (promptText) => {
      const selected = sections.find(
        (section) =>
          String(section.id ?? section.Id) === String(formData.sectionId)
      );
      const sectionName =
        selected?.sectionName || selected?.SectionName || "";

      return {
        SectionName: sectionName,
        SubSectionName: formData.subSectionName || "",
        UserPrompt: promptText || "",
      };
    },
    [sections, formData.sectionId, formData.subSectionName]
  );

  return (
    <>
      <FormSideDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        title={editMode ? "Update Sub Section" : "Add Sub Section"}
        subtitle={
          editMode
            ? "Edit sub section details and save changes"
            : "Fill in the details to create a new sub section"
        }
        icon={editMode ? "ri-pencil-line" : "ri-stack-line"}
        width="lg"
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeDrawer}
              disabled={isButtonDisabled}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="subsection-side-form"
              className="btn btn-secondary"
              disabled={isButtonDisabled}
            >
              {isButtonDisabled
                ? editMode
                  ? "Updating..."
                  : "Saving..."
                : editMode
                  ? "Update"
                  : "Save"}
            </button>
          </>
        }
      >
        <form id="subsection-side-form" onSubmit={handleSubmit} method="POST">
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="sectionId" className="form-label">
                Section <span className="required-field">*</span>
              </label>
              <select
                name="sectionId"
                id="sectionId"
                value={formData.sectionId}
                onChange={handleInputChange}
                className={`form-select ${
                  errors.sectionId ? "is-invalid" : ""
                }`}
              >
                <option value="">Select section</option>
                {sections.map((section) => {
                  const sid = section.id ?? section.Id;
                  return (
                    <option key={sid} value={String(sid)}>
                      {section.sectionName || section.SectionName || "-"}
                    </option>
                  );
                })}
              </select>
              {errors.sectionId && (
                <div className="invalid-feedback">{errors.sectionId}</div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="subSectionName" className="form-label">
                Sub Section Name <span className="required-field">*</span>
              </label>
              <input
                type="text"
                name="subSectionName"
                id="subSectionName"
                placeholder="Enter sub section name"
                value={formData.subSectionName}
                onChange={handleInputChange}
                className={`form-control ${
                  errors.subSectionName ? "is-invalid" : ""
                }`}
              />
              {errors.subSectionName && (
                <div className="invalid-feedback">
                  {errors.subSectionName}
                </div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="subSectionSlug" className="form-label">
                Sub Section Slug <span className="required-field">*</span>
              </label>
              <input
                type="text"
                name="subSectionSlug"
                id="subSectionSlug"
                placeholder="sub-section-slug"
                value={formData.subSectionSlug}
                onChange={handleInputChange}
                className={`form-control ${
                  errors.subSectionSlug ? "is-invalid" : ""
                }`}
              />
              {errors.subSectionSlug && (
                <div className="invalid-feedback">
                  {errors.subSectionSlug}
                </div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="displayOrder" className="form-label">
                Display Order <span className="required-field">*</span>
              </label>
              <input
                type="number"
                name="displayOrder"
                id="displayOrder"
                min="1"
                step="1"
                placeholder="e.g. 1"
                value={formData.displayOrder}
                onChange={handleDisplayOrderChange}
                onKeyDown={handleDisplayOrderKeyDown}
                className={`form-control ${
                  errors.displayOrder ? "is-invalid" : ""
                }`}
              />
              {errors.displayOrder && (
                <div className="invalid-feedback">{errors.displayOrder}</div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="subSectionDescription" className="form-label">
                Sub Section Description
              </label>
              <textarea
                name="subSectionDescription"
                id="subSectionDescription"
                rows="3"
                placeholder="Brief description for this sub section"
                value={formData.subSectionDescription}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-light">
                <p className="text-muted small mb-3 fw-medium">Visibility</p>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label d-block">Featured</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isFeatured"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleToggleChange}
                      />
                      <label className="form-check-label" htmlFor="isFeatured">
                        Show as featured
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label d-block">Featured Home</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isFeaturedHome"
                        name="isFeaturedHome"
                        checked={formData.isFeaturedHome}
                        onChange={handleToggleChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="isFeaturedHome"
                      >
                        Show on home page
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label d-block">Premium</label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="isPremium"
                        name="isPremium"
                        checked={formData.isPremium}
                        onChange={handleToggleChange}
                      />
                      <label className="form-check-label" htmlFor="isPremium">
                        Mark as premium
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded p-3 bg-light">
                <p className="text-muted small mb-3 fw-medium">SEO Settings</p>
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="metaTitle" className="form-label">
                      Meta Title
                    </label>
                    <input
                      type="text"
                      name="metaTitle"
                      id="metaTitle"
                      value={formData.metaTitle}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="metaKeywords" className="form-label">
                      Meta Keywords
                    </label>
                    <input
                      type="text"
                      name="metaKeywords"
                      id="metaKeywords"
                      value={formData.metaKeywords}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="metaDescription" className="form-label">
                      Meta Description
                    </label>
                    <textarea
                      name="metaDescription"
                      id="metaDescription"
                      rows="2"
                      value={formData.metaDescription}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormSideDrawer>

      <AiAssistantOffcanvas
        pageLink="manage-sub-sections"
        formData={formData}
        setFormData={setFormData}
        fieldMap={SUB_SECTION_AI_RESPONSE_FIELD_MAP}
        buildDynamicFields={buildSubSectionDynamicFields}
      />
    </>
  );
};
